import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { IconType } from 'react-icons';
import { BsApple } from 'react-icons/bs';
import { FcGoogle } from 'react-icons/fc';
import { TbFaceId, TbFingerprint, TbLink, TbScanEye } from 'react-icons/tb';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { API, getRequest } from '@/api/api.ts';
import { hasAuthedOnDevice } from '@/api/tokens.helper';
import type { ApiException } from '@/common/error';
import { Modal } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { ContactInput, Input, PasswordInput } from '@/components/Inputs';
import { INVALID_CONTACT_MESSAGE } from '@/components/Inputs/contactMessages';
import { checkLogin, type TwoFactorChallengeMethod } from '@/store/auth/auth.api';
import { useAuth } from '@/store/auth/auth.store.ts';
import { consumeRedirectAfterAuth } from '@/utils/auth-redirect';
import {
  type BiometryKind,
  canUseBiometric,
  enableBiometricLogin,
  getBiometryInfo,
  getBiometryLabel,
  isBiometricEnabled,
} from '@/utils/biometric';
import { captureReferralCode, getReferralCode } from '@/utils/referral';
import { pickSavedCredentials, promptSaveCredentials } from '@/utils/savePassword';
import logo from '@assets/white-logo.svg';
import { useIsMobile } from '@hooks/useIsMobile.tsx';
import styles from '@pages/Auth/Auth.module.scss';
import OtpCode from '@pages/Auth/OtpCode/OtpCode.tsx';

const AuthForm = () => {
  const isMobile = useIsMobile();
  // A referral link lands here as /auth?ref=CODE. Capture it before anything below
  // renders, then let each piece read it back from storage — it has to outlive this
  // screen anyway (the code is only sent at the end of /sign-up).
  useState(() => captureReferralCode(window.location.search));

  return isMobile ? <MobileAuthForm /> : <DesktopAuthForm />;
};

export default AuthForm;

const MobileAuthForm = () => {
  const { t } = useTranslation();
  const invited = !!getReferralCode();

  return (
    <div className={styles.mobileAuthPage}>
      <img src={logo} alt="VesselHaven" className={styles.authLogo} />
      <h1 className={styles.welcomeBack}>
        {invited
          ? t('authPage.invitedHeading', { defaultValue: 'You’re Invited' })
          : t('authPage.welcomeBack', { defaultValue: 'Welcome Back' })}
      </h1>
      <FormContainer />
    </div>
  );
};

type AuthMode = 'login' | 'register';

const AuthTabs = ({ mode, onChange }: { mode: AuthMode; onChange: (m: AuthMode) => void }) => {
  const { t } = useTranslation();
  const tabClass = (tab: AuthMode) =>
    mode === tab ? `${styles.authTab} ${styles.authTabActive}` : styles.authTab;

  return (
    <nav className={styles.authTabs}>
      <button type="button" className={tabClass('login')} onClick={() => onChange('login')}>
        {t('authPage.tabSignIn', { defaultValue: 'Sign In' })}
      </button>
      <button type="button" className={tabClass('register')} onClick={() => onChange('register')}>
        {t('authPage.tabCreateAccount', { defaultValue: 'Create an Account' })}
      </button>
    </nav>
  );
};

const DesktopAuthForm = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.desktopAuthPage}>
      <div className={styles.desktopAuthContainer}>
        <div className={styles.greetingWrapper}>
          <img alt="logo" src={logo} style={{ marginBottom: 24 }} />
          <h1 className={styles.heading}>{t('authPage.headingExplore')}</h1>
          <p className={styles.headingText}>{t('authPage.headingGoal')}</p>
        </div>
        <div className={styles.formWrapper}>
          <FormContainer />
        </div>
      </div>
    </div>
  );
};

const loginField = z
  .string()
  .transform((value) => value.trim())
  .pipe(
    z
      .string()
      .min(1, 'Please enter an email or mobile number.')
      .refine((value) => z.string().email().safeParse(value).success || isValidPhoneNumber(value), {
        message: INVALID_CONTACT_MESSAGE,
      }),
  );

const schema = z.object({
  login: loginField,
  password: z
    .string()
    .min(1, 'Please enter a password.')
    .min(8, 'Please use at least 8 characters for the password.')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/,
      'Please choose a stronger password.',
    ),
});

// Create-an-Account tab only collects the email/phone; password is set later in /sign-up.
const registerSchema = z.object({ login: loginField });

type FormValues = z.infer<typeof schema>;
type RegisterValues = z.infer<typeof registerSchema>;

// Email-only form for the "Create an Account" tab. On submit it checks the address is free,
// then hands it to the /sign-up flow (which collects name + password and calls /register).
const RegisterEmailForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invited = !!getReferralCode();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { login: '' },
    mode: 'onChange',
  });

  const onSubmit = async ({ login }: RegisterValues) => {
    try {
      await checkLogin(login);
    } catch (err) {
      setError('login', {
        message:
          (err as ApiException | undefined)?.type === 'DUPLICATE'
            ? t('authPage.errors.accountExists', {
                defaultValue:
                  'An account with this email or mobile number already exists. Please go to Sign In instead.',
              })
            : t('authPage.errors.checkLoginFailed', {
                defaultValue: 'Couldn’t check this email or mobile number. Please try again.',
              }),
      });
      return;
    }
    navigate('/sign-up', { state: { email: login } });
  };

  return (
    <form className={styles.authForm} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.inputsCard}>
        {invited && (
          <p className={styles.invitedNote}>
            {t('authPage.invitedNote', {
              defaultValue: 'A friend invited you. Please create your account to accept.',
            })}
          </p>
        )}

        <Controller
          control={control}
          name="login"
          render={({ field }) => (
            <ContactInput
              label="Email or Mobile Number"
              name="username"
              autoComplete="username"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={errors.login?.message}
            />
          )}
        />

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? t('common.loading', { defaultValue: 'Loading…' })
            : t('authPage.signup.continue', { defaultValue: 'Continue' })}
        </Button>

        <p className={styles.poweredBy}>
          {t('authPage.poweredBy', { defaultValue: 'powered by' })}{' '}
          <span>
            <span style={{ fontWeight: 700 }}>You</span>biquity
          </span>
        </p>
      </div>
    </form>
  );
};

// Set once the user declines the "enable biometric login" offer, so we stop nagging.
const BIO_OFFER_DISMISSED = 'BIOMETRIC_OFFER_DISMISSED';

const BIOMETRY_ICONS: Record<BiometryKind, IconType> = {
  face: TbFaceId,
  fingerprint: TbFingerprint,
  iris: TbScanEye,
  biometrics: TbFingerprint,
};

type TwoFactorMethodsStatus = {
  default: string;
  email: boolean;
  totp: boolean;
  sms: boolean;
  call: boolean;
};

// Pick which code-based channel to challenge: honour the user's default when it's a
// code method, otherwise fall back to the first enabled one. TOTP is handled separately.
const pickCodeMethod = (methods?: TwoFactorMethodsStatus): TwoFactorChallengeMethod | null => {
  if (!methods) return null;
  const codeMethods: TwoFactorChallengeMethod[] = ['email', 'sms', 'call'];
  if (codeMethods.includes(methods.default as TwoFactorChallengeMethod)) {
    return methods.default as TwoFactorChallengeMethod;
  }
  return codeMethods.find((m) => methods[m]) ?? null;
};

const CODE_METHOD_LABELS: Record<TwoFactorChallengeMethod, string> = {
  email: 'email',
  sms: 'SMS',
  call: 'phone call',
};

const FormContainer = () => {
  const { t } = useTranslation();
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [challengeMethod, setChallengeMethod] = useState<TwoFactorChallengeMethod | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSetupOtpModalOpen, setIsSetupOtpModalOpen] = useState(false);
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [isRecovering, setIsRecovering] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoadingQr, setIsLoadingQr] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  // const [keepSignedIn, setKeepSignedIn] = useState(false);
  // An invite link is an invitation to sign up, so it opens the register tab. The user
  // can still switch to Sign In — a referred user who already has an account.
  // `?mode=` wins over that guess: pages that send the user here for a specific action
  // (the event invite's "Register" vs "Log in") say which tab they meant. With none of
  // those, a device someone has signed in on opens Sign In and a new one opens Create Account.
  const invitedBy = getReferralCode();
  const [searchParams] = useSearchParams();
  const requestedMode = searchParams.get('mode');
  const location = useLocation();
  // A finished password reset links here with its login, so the user only types the password.
  const prefillLogin = (location.state as { email?: string } | null)?.email ?? '';
  const [mode, setMode] = useState<AuthMode>(() =>
    prefillLogin
      ? 'login'
      : requestedMode === 'register' || requestedMode === 'login'
        ? requestedMode
        : invitedBy || !hasAuthedOnDevice()
          ? 'register'
          : 'login',
  );
  const {
    login,
    loginWithBiometric,
    twoFactorToken,
    verifyTotp,
    sendTwoFactorCode,
    verifyTwoFactorCode,
    verifyRecoveryCode,
    clearTwoFactor,
    enableTotp,
    completeOAuth,
    sendMagicLink,
  } = useAuth();

  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const [bioReady, setBioReady] = useState(false);
  const [bioLabel, setBioLabel] = useState('Biometrics');
  const [bioKind, setBioKind] = useState<BiometryKind>('biometrics');
  const [bioEnableToken, setBioEnableToken] = useState<string | null>(null);

  const navigate = useNavigate();
  // Replace: Back from the app must not return to the sign-in form.
  const enterApp = () => navigate(consumeRedirectAfterAuth() ?? '/dashboard', { replace: true });

  // Magic-link and OAuth land here after a 2FA challenge: they stash the challenge token
  // in the store and redirect to /auth?showOtp. Pick that token up and open the right
  // modal — TOTP, or an emailed / SMS / call code.
  useEffect(() => {
    if (!(location.state as { showOtp?: boolean } | null)?.showOtp) return;

    const { twoFactorToken: stashedToken, twoFactorMethods: stashedMethods } = useAuth.getState();
    if (!stashedToken) return;

    if (stashedMethods?.totp) {
      setIsOtpModalOpen(true);
      return;
    }

    const codeMethod = pickCodeMethod(stashedMethods ?? undefined);
    if (!codeMethod) return;

    setChallengeMethod(codeMethod);
    setIsCodeModalOpen(true);
    setOtpError('');
    setIsSendingCode(true);
    useAuth
      .getState()
      .sendTwoFactorCode(codeMethod)
      .catch((err) => {
        console.error('Failed to send 2FA code:', err);
        setOtpError('Couldn’t send the code. Please tap Resend Code to try again.');
      })
      .finally(() => setIsSendingCode(false));
  }, [location.state]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (isBiometricEnabled() && (await canUseBiometric())) {
        const { label, kind } = await getBiometryInfo();
        if (!active) return;
        setBioLabel(label);
        setBioKind(kind);
        setBioReady(true);
        // Auto-prompt on launch so a returning user just sees the biometric sheet.
        // Chrome/Edge/Android allow the prompt without a user gesture; Safari/iOS
        // reject it (NotAllowedError), in which case loginWithBiometric resolves
        // false and the button stays visible as a manual fallback.
        const ok = await loginWithBiometric();
        if (ok && active) {
          enterApp();
        }
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Web OAuth navigates away via window.location.href. Pressing back restores this page
  // from the bfcache frozen with isOAuthLoading=true, leaving the social buttons disabled.
  // A bfcache restore fires pageshow with persisted=true — reset the flag then.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setIsOAuthLoading(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const handleBiometric = async () => {
    const ok = await loginWithBiometric();
    if (ok) {
      enterApp();
    }
  };

  // After a successful login, offer to enable biometric login for next time.
  // Both native and web defer to the same branded modal: native enable just stores
  // the token (no gesture needed), and web needs the modal button to run WebAuthn
  // credential creation inside a fresh user gesture. Returns true when deferred, so
  // the caller knows not to navigate — the modal handles navigation after the choice.
  const maybeOfferBiometric = async (refreshToken?: string): Promise<boolean> => {
    if (
      !refreshToken ||
      isBiometricEnabled() ||
      window.localStorage.getItem(BIO_OFFER_DISMISSED) === 'true' ||
      !(await canUseBiometric())
    ) {
      return false;
    }

    const label = await getBiometryLabel();

    // Native (iOS/Android): storing the token needs no user gesture, so offer with a
    // blocking confirm and enable inline, then let the caller navigate normally. This
    // restores the original, reliable native flow. The deferred React modal below is
    // web-only — there WebAuthn credential creation must run inside the Enable tap, so
    // navigation has to wait for that gesture.
    if (Capacitor.isNativePlatform()) {
      const accepted = window.confirm(
        t('authPage.enableBiometric', {
          method: label,
          defaultValue: `Would you like to use ${label} to sign in faster next time?`,
        }),
      );
      if (accepted) {
        try {
          await enableBiometricLogin(refreshToken);
        } catch (err) {
          console.error('Failed to enable biometric login:', err);
        }
      } else {
        window.localStorage.setItem(BIO_OFFER_DISMISSED, 'true');
      }
      return false; // never block navigation on native
    }

    setBioLabel(label);
    setBioEnableToken(refreshToken);
    return true;
  };

  // Web passkey enable flow: stash the refresh token, then enable on an explicit tap.
  const finishBioOffer = () => {
    setBioEnableToken(null);
    enterApp();
  };

  // Declined the offer (close / "Not now"): remember it so we don't ask again.
  const dismissBioOffer = () => {
    window.localStorage.setItem(BIO_OFFER_DISMISSED, 'true');
    finishBioOffer();
  };

  const handleEnableBio = async () => {
    if (bioEnableToken) {
      try {
        await enableBiometricLogin(bioEnableToken);
      } catch (err) {
        console.error('Failed to enable passkey login:', err);
      }
    }
    finishBioOffer();
  };

  const handleMagicLink = async () => {
    const email = loginValue.trim();
    if (!email) {
      setError('login', {
        type: 'manual',
        message: 'Please enter your email to get a magic link.',
      });
      return;
    }
    if (!z.string().email().safeParse(email).success) {
      setError('login', {
        type: 'manual',
        message: 'Please enter a valid email to get a magic link.',
      });
      return;
    }
    setIsMagicLinkLoading(true);
    try {
      await sendMagicLink(email);
      setMagicLinkSent(true);
    } catch {
      setError('root', {
        type: 'server',
        message: 'Couldn’t send the magic link. Please try again.',
      });
    } finally {
      setIsMagicLinkLoading(false);
    }
  };

  const {
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      login: prefillLogin,
      password: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (values: FormValues) => {
    clearErrors();
    setOtpError('');

    try {
      const result = await login(values.login, values.password);
      if (result.two_factor_enabled) {
        const twoFactorStatus = result.two_factor_methods;

        if (twoFactorStatus?.totp) {
          setIsOtpModalOpen(true);
        } else {
          const codeMethod = pickCodeMethod(twoFactorStatus);
          if (codeMethod) {
            await startCodeChallenge(codeMethod);
          } else {
            setIsSetupOtpModalOpen(true);
          }
        }
        return;
      }

      await promptSaveCredentials(values.login, values.password);
      const deferred = await maybeOfferBiometric(result.tokens?.refresh_token);
      if (deferred) return; // the passkey-enable modal will navigate after the user decides

      enterApp();
    } catch {
      setError('root', { type: 'server', message: `${t('authPage.errors.somethingWrong')}` });
    }
  };

  // Native: offer the saved accounts on open and sign in with the one picked. Skipped when
  // the biometric sheet or a 2FA modal is about to take the screen.
  useEffect(() => {
    if (
      mode !== 'login' ||
      isBiometricEnabled() ||
      (location.state as { showOtp?: boolean } | null)?.showOtp
    ) {
      return;
    }
    let active = true;
    pickSavedCredentials().then((creds) => {
      if (!creds || !active) return;
      setValue('login', creds.username);
      setValue('password', creds.password);
      void handleSubmit(onSubmit)();
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerifyTotp = async (code: string) => {
    if (!twoFactorToken) {
      setOtpError('Couldn’t verify your sign-in. Please sign in again.');
      setIsOtpModalOpen(false);
      clearTwoFactor();
      return;
    }

    setOtpError('');
    setIsVerifying(true);
    try {
      const ok = await verifyTotp({ token: twoFactorToken, code });
      if (ok) {
        setIsOtpModalOpen(false);
        await promptSaveCredentials(...getValues(['login', 'password']));
        enterApp();
      } else {
        setOtpError('Couldn’t verify the code. Please try again.');
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 400) {
        setOtpError('That code doesn’t match. Please try again.');
      } else {
        setOtpError('Couldn’t verify the code. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Email / SMS / call challenge: send a code, then verify it on the same modal.
  const startCodeChallenge = async (method: TwoFactorChallengeMethod) => {
    setChallengeMethod(method);
    setOtpError('');
    setIsCodeModalOpen(true);
    setIsSendingCode(true);
    try {
      await sendTwoFactorCode(method);
    } catch (err) {
      console.error('Failed to send 2FA code:', err);
      setOtpError('Couldn’t send the code. Please tap Resend Code to try again.');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (!challengeMethod) return;
    setOtpError('');
    setIsVerifying(true);
    try {
      const ok = await verifyTwoFactorCode(challengeMethod, code);
      if (ok) {
        setIsCodeModalOpen(false);
        setChallengeMethod(null);
        await promptSaveCredentials(...getValues(['login', 'password']));
        enterApp();
      } else {
        setOtpError('That code doesn’t match. Please try again.');
      }
    } catch (err) {
      if (isAxiosError(err) && (err.response?.status === 400 || err.response?.status === 401)) {
        setOtpError('That code doesn’t match. Please try again.');
      } else {
        setOtpError('Couldn’t verify the code. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!challengeMethod) return;
    setOtpError('');
    setIsSendingCode(true);
    try {
      await sendTwoFactorCode(challengeMethod);
    } catch (err) {
      console.error('Failed to resend 2FA code:', err);
      setOtpError('Couldn’t resend the code. Please try again.');
    } finally {
      setIsSendingCode(false);
    }
  };

  // Switch from a challenge modal to recovery without dropping the 2FA token (the
  // recovery call needs it), so we close the modals directly instead of via onClose.
  const openRecovery = () => {
    setIsOtpModalOpen(false);
    setIsCodeModalOpen(false);
    setRecoveryError('');
    setRecoveryCode('');
    setIsRecoveryModalOpen(true);
  };

  const handleVerifyRecovery = async () => {
    const code = recoveryCode.trim();
    if (!code) return;
    setRecoveryError('');
    setIsRecovering(true);
    try {
      const ok = await verifyRecoveryCode(code);
      if (ok) {
        setIsRecoveryModalOpen(false);
        await promptSaveCredentials(...getValues(['login', 'password']));
        enterApp();
      } else {
        setRecoveryError('That recovery code doesn’t match. Please try again.');
      }
    } catch (err) {
      if (isAxiosError(err) && (err.response?.status === 400 || err.response?.status === 401)) {
        setRecoveryError('That recovery code doesn’t match. Please try again.');
      } else {
        setRecoveryError('Couldn’t verify the recovery code. Please try again.');
      }
    } finally {
      setIsRecovering(false);
    }
  };

  const handleSkipSetup = async () => {
    setIsSetupOtpModalOpen(false);
    clearTwoFactor();
    await promptSaveCredentials(...getValues(['login', 'password']));
    enterApp();
  };

  const handleSetupTotp = async () => {
    setIsLoadingQr(true);
    try {
      const response = await enableTotp();
      setQrCode(response.qr_code);
    } catch (err) {
      console.error('Failed to enable TOTP:', err);
      setError('root', {
        type: 'server',
        message: 'Couldn’t set up two-factor authentication. Please try again.',
      });
    } finally {
      setIsLoadingQr(false);
    }
  };

  const handleQrCodeContinue = () => {
    setIsSetupOtpModalOpen(false);
    setQrCode('');
    setIsOtpModalOpen(true);
  };

  const oauthHandler = async (provider: 'google' | 'apple') => {
    setIsOAuthLoading(true);
    try {
      const isNative = Capacitor.isNativePlatform();
      const response = await getRequest<{ link: string }>(
        API.AUTH,
        `/oauth/${provider}/link${isNative ? '?platform=mobile' : ''}`,
      );
      const link = response.data?.link;
      if (link) {
        const url = new URL(link);
        const state = url.searchParams.get('state');
        if (state) {
          sessionStorage.setItem(`oauth:${provider}:state`, state);
        }
        url.searchParams.set('prompt', 'select_account');

        if (isNative) {
          // Two ways out of the in-app browser: a successful OAuth deep-link (appUrlOpen)
          // or the user dismissing it (browserFinished). Whichever fires first wins via
          // `settled`; cleanup() removes both listeners so cancelled attempts don't leak a
          // listener and don't leave isOAuthLoading stuck true (buttons disabled forever).
          let settled = false;
          const listeners: PluginListenerHandle[] = [];
          const cleanup = () => listeners.forEach((l) => l.remove());

          listeners.push(
            await CapacitorApp.addListener('appUrlOpen', async ({ url: deepLink }) => {
              if (settled) return;
              settled = true;
              cleanup();
              await Browser.close();

              const callbackUrl = new URL(deepLink);
              const params = Object.fromEntries(callbackUrl.searchParams.entries());
              const oauthError = params.error;
              if (oauthError) {
                setError('root', { type: 'server', message: oauthError });
                setIsOAuthLoading(false);
                return;
              }

              const receivedState = params.state;
              const expectedState = sessionStorage.getItem(`oauth:${provider}:state`);
              if (expectedState && expectedState !== receivedState) {
                setError('root', {
                  type: 'server',
                  message: 'Couldn’t complete sign-in. Please try again.',
                });
                setIsOAuthLoading(false);
                return;
              }

              try {
                const result = await completeOAuth(provider, params);
                sessionStorage.removeItem(`oauth:${provider}:state`);
                if (result.two_factor_enabled && result.two_factor_token) {
                  navigate('/auth', { replace: true, state: { showOtp: true } });
                } else if (result.require_reset_password) {
                  navigate('/forgot-password', { replace: true, state: { fromOAuth: true } });
                } else {
                  await maybeOfferBiometric(result.tokens?.refresh_token);
                  const session = useAuth.getState().session;
                  const isActivated =
                    session?.activation_status === 'registered' ||
                    session?.activation_status === 'active';
                  navigate(
                    isActivated ? (consumeRedirectAfterAuth() ?? '/dashboard') : '/onboarding',
                    { replace: true },
                  );
                }
              } catch {
                setError('root', {
                  type: 'server',
                  message: 'Couldn’t sign you in. Please try again.',
                });
              } finally {
                setIsOAuthLoading(false);
              }
            }),
          );

          // User dismissed the in-app browser without completing OAuth — unstick the buttons.
          listeners.push(
            await Browser.addListener('browserFinished', () => {
              if (settled) return;
              settled = true;
              cleanup();
              setIsOAuthLoading(false);
            }),
          );

          await Browser.open({ url: url.toString() });
        } else {
          window.location.href = url.toString();
        }
      }
    } catch (error) {
      console.error('OAuth error:', error);
      setError('root', { type: 'server', message: 'Couldn’t start sign-in. Please try again.' });
      setIsOAuthLoading(false);
    }
  };

  const loginValue = useWatch({ control, name: 'login' });

  return (
    <>
      <AuthTabs mode={mode} onChange={setMode} />
      {mode === 'login' ? (
        <form className={styles.authForm} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.inputsCard}>
            <Controller
              control={control}
              name="login"
              render={({ field }) => (
                <ContactInput
                  label="Email or Mobile Number"
                  name="username"
                  autoComplete="username"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.login?.message}
                />
              )}
            />

            <PasswordInput
              label="Password"
              placeholder="Must be 8 characters"
              autoComplete="current-password"
              {...register('password', {
                onChange: () => clearErrors('root'),
              })}
              error={errors.password ? errors.password.message : errors.root?.message}
            />

            <div className={styles.optionsRow}>
              {/* <label className={styles.keepSignedIn}>
                <input
                  type="checkbox"
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                />
                <span className={styles.checkbox} />
                {t('authPage.keepSignedIn', { defaultValue: 'Keep me signed in' })}
              </label> */}
              <NavLink to="/forgot-password" className={styles.forgotLink}>
                {t('authPage.forgotPassword.heading')}
              </NavLink>
            </div>

            <Button type="submit">{t('authPage.signIn', { defaultValue: 'Sign In' })}</Button>

            <p className={styles.poweredBy}>
              {t('authPage.poweredBy', { defaultValue: 'powered by' })}{' '}
              <span>
                <span style={{ fontWeight: 700 }}>You</span>biquity
              </span>
            </p>
          </div>
        </form>
      ) : (
        <RegisterEmailForm />
      )}

      <div className={styles.continueDivider}>
        <span />
        <p>{t('authPage.continueWith', { defaultValue: 'or continue with' })}</p>
        <span />
      </div>

      <div className={styles.socialGroup}>
        {mode === 'login' &&
          bioReady &&
          (() => {
            const BioIcon = BIOMETRY_ICONS[bioKind];
            return (
              <Button className={styles.socialButton} type="button" onClick={handleBiometric}>
                <BioIcon />{' '}
                {t('authPage.loginWithBiometric', {
                  method: bioLabel,
                  defaultValue: `Sign In with ${bioLabel}`,
                })}
              </Button>
            );
          })()}

        <Button
          className={styles.socialButton}
          type="button"
          onClick={() => oauthHandler('google')}
          disabled={isOAuthLoading}
        >
          <FcGoogle /> {t('authPage.buttonGoogle')}
        </Button>

        <Button
          className={styles.socialButton}
          type="button"
          onClick={() => oauthHandler('apple')}
          disabled={isOAuthLoading}
        >
          <BsApple /> {t('authPage.buttonApple')}
        </Button>

        {mode === 'login' && (
          <Button
            className={styles.socialButton}
            type="button"
            onClick={handleMagicLink}
            disabled={isMagicLinkLoading || magicLinkSent}
          >
            <TbLink />
            {magicLinkSent
              ? t('authPage.magicLinkSent', { defaultValue: 'Magic Link Sent' })
              : isMagicLinkLoading
                ? t('authPage.magicLinkSending', { defaultValue: 'Sending…' })
                : t('authPage.magicLink', { defaultValue: 'Send Magic Link' })}
          </Button>
        )}
      </div>

      {/* OTP Verification Modal */}
      {isOtpModalOpen && (
        <Modal
          open={isOtpModalOpen}
          onClose={() => {
            setIsOtpModalOpen(false);
            clearTwoFactor();
          }}
          title="Enter Authenticator Code"
        >
          <OtpCode
            onSubmit={handleVerifyTotp}
            isLoading={isVerifying}
            error={otpError}
            submitLabel="Verify"
          />
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 16 }}>
            <ButtonOutlined type="button" onClick={openRecovery}>
              Use Recovery Code
            </ButtonOutlined>
          </div>
        </Modal>
      )}

      {/* Email / SMS / call code challenge */}
      {isCodeModalOpen && challengeMethod && (
        <Modal
          open={isCodeModalOpen}
          onClose={() => {
            setIsCodeModalOpen(false);
            setChallengeMethod(null);
            clearTwoFactor();
          }}
          title={`Enter Code Sent by ${CODE_METHOD_LABELS[challengeMethod]}`}
        >
          <OtpCode
            onSubmit={handleVerifyCode}
            isLoading={isVerifying || isSendingCode}
            error={otpError}
            submitLabel="Verify"
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              alignItems: 'center',
              paddingBottom: 16,
            }}
          >
            <ButtonOutlined type="button" onClick={handleResendCode} disabled={isSendingCode}>
              {isSendingCode ? 'Sending…' : 'Resend Code'}
            </ButtonOutlined>
            <ButtonOutlined type="button" onClick={openRecovery}>
              Use Recovery Code
            </ButtonOutlined>
          </div>
        </Modal>
      )}

      {/* Recovery code: wipe all 2FA and sign in when the user lost their device */}
      {isRecoveryModalOpen && (
        <Modal
          open={isRecoveryModalOpen}
          onClose={() => {
            setIsRecoveryModalOpen(false);
            setRecoveryCode('');
            setRecoveryError('');
            clearTwoFactor();
          }}
          title="Use Recovery Code"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 20 }}>
            <p style={{ margin: 0, textAlign: 'center', color: '#fff' }}>
              Please enter one of your recovery codes. This turns off two-factor authentication,
              which you can turn back on in Security.
            </p>
            <Input
              label="Recovery Code"
              placeholder="ABCD-EFGH-IJKL"
              autoComplete="one-time-code"
              value={recoveryCode}
              onChange={(e) => {
                setRecoveryCode(e.target.value);
                setRecoveryError('');
              }}
              error={recoveryError}
            />
            <Button type="button" onClick={handleVerifyRecovery} disabled={isRecovering}>
              {isRecovering ? 'Verifying…' : 'Verify'}
            </Button>
          </div>
        </Modal>
      )}

      {/* Setup OTP Modal */}
      {isSetupOtpModalOpen && (
        <Modal
          open={isSetupOtpModalOpen}
          onClose={() => {
            setIsSetupOtpModalOpen(false);
            setQrCode('');
            clearTwoFactor();
          }}
          title="Set Up Two-Factor Authentication"
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '20px',
              alignItems: 'center',
            }}
          >
            {!qrCode ? (
              <>
                <p style={{ margin: 0, textAlign: 'center' }}>
                  Would you like to set up two-factor authentication?
                </p>
                <div
                  className={styles.twoFAButtons}
                  style={{ display: 'flex', gap: '12px', width: '100%' }}
                >
                  <ButtonOutlined onClick={handleSkipSetup}>Skip</ButtonOutlined>
                  <Button onClick={handleSetupTotp} disabled={isLoadingQr}>
                    {isLoadingQr ? 'Loading…' : 'Set Up'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: 0, textAlign: 'center', color: '#475569' }}>
                  Please scan the QR code to finish setting up two-factor authentication.
                </p>
                <img
                  src={qrCode}
                  alt="QR Code"
                  style={{
                    width: '256px',
                    height: '256px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                  }}
                />
                <Button onClick={handleQrCodeContinue}>Continue</Button>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Biometric enable offer. Native stores the token directly; web runs WebAuthn
          credential creation inside the Enable tap (a fresh user gesture). */}
      {bioEnableToken && (
        <Modal open={!!bioEnableToken} onClose={dismissBioOffer} title="Faster Sign-In">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '20px',
              alignItems: 'center',
            }}
          >
            <p style={{ margin: 0, textAlign: 'center' }}>
              {t('authPage.enableBiometric', {
                method: bioLabel,
                defaultValue: `Would you like to use ${bioLabel} to sign in faster next time?`,
              })}
            </p>
            <div
              style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}
            >
              <ButtonOutlined onClick={dismissBioOffer}>
                {t('authPage.notNow', { defaultValue: 'Not Now' })}
              </ButtonOutlined>
              <Button onClick={handleEnableBio}>
                {t('authPage.enable', { defaultValue: 'Enable' })}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
